/**
 * The families of the selected Revit's version — v2's `FamilyArtifactList`: one
 * card per family, newest work first as the agent lists them, and New Component.
 * Families are stored per Revit version, so the list follows the instance picked
 * in the title bar.
 */
import { useCallback, useEffect, useState } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import { BoxIcon, ClockIcon, LoaderIcon, PlusIcon } from "../components/skills/icons";
import type { FamilyBuilderClient } from "./api";
import { FamilyBuilderError } from "./errors";
import type { FamilyArtifactMetadata } from "./types";

type ListState =
  | { kind: "loading" }
  | { kind: "unsupported" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; revitVersion: string | null; artifacts: FamilyArtifactMetadata[] };

export function FamilyBuilderList({
  client,
  head,
  onOpen,
}: {
  client: FamilyBuilderClient;
  /** The pane's title row; the list adds New Component to it. */
  head: (actions: React.ReactNode, version: string | null) => React.ReactNode;
  onOpen: (artifactId: string) => void;
}) {
  const { t, locale } = useLocale();
  const [state, setState] = useState<ListState>({ kind: "loading" });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const load = useCallback(
    (isCurrent: () => boolean = () => true) => {
      void client
        .list()
        .then((list) => {
          if (isCurrent()) setState({ kind: "loaded", ...list });
        })
        .catch((error: unknown) => {
          if (!isCurrent()) return;
          // An agent from before the Family Builder has no such route.
          if (error instanceof FamilyBuilderError && error.status === 404) {
            setState({ kind: "unsupported" });
            return;
          }
          setState({ kind: "error", message: error instanceof Error ? error.message : String(error) });
        });
    },
    [client],
  );

  // Asked again when the window comes back: the user works in Revit beside the app.
  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    load(() => !cancelled);
    const onFocus = () => load(() => !cancelled);
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  const create = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const created = await client.create();
      onOpen(created.id);
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : String(error));
      setCreating(false);
    }
  };

  const actions = (
    <button
      type="button"
      className="settings-btn family-builder-new"
      disabled={creating || state.kind === "unsupported" || state.kind === "loading"}
      onClick={() => void create()}
    >
      {creating ? <LoaderIcon size={14} /> : <PlusIcon size={14} />}
      {t("familyBuilder.newComponent")}
    </button>
  );

  let body: React.ReactNode;
  if (state.kind === "loading") {
    body = (
      <div className="skills-center muted">
        <LoaderIcon size={14} /> {t("common.loading")}
      </div>
    );
  } else if (state.kind === "unsupported") {
    body = <p className="family-builder-empty-line">{t("familyBuilder.unsupported")}</p>;
  } else if (state.kind === "error") {
    body = <p className="settings-msg err">{state.message}</p>;
  } else if (state.artifacts.length === 0) {
    body = (
      <div className="settings-card skills-empty">
        <span className="skills-empty-glyph">
          <BoxIcon size={22} />
        </span>
        <p className="skills-empty-title">{t("familyBuilder.listEmptyTitle")}</p>
        <p className="skills-empty-desc">{t("familyBuilder.listEmptyDescription")}</p>
      </div>
    );
  } else {
    body = (
      <div className="family-builder-grid">
        {state.artifacts.map((artifact) => {
          const at = artifact.artifact.executionAt ? new Date(artifact.artifact.executionAt) : null;
          return (
            <button key={artifact.id} type="button" className="family-builder-card" data-artifact={artifact.id} onClick={() => onOpen(artifact.id)}>
              <span className="family-builder-card-head">
                <span className="family-builder-glyph" aria-hidden="true">
                  <BoxIcon size={18} />
                </span>
                <span className="family-builder-card-text">
                  <span className="family-builder-card-title">{artifact.artifact.displayName || artifact.artifact.name}</span>
                  <span className="family-builder-card-desc">{artifact.artifact.description || t("familyBuilder.noDescription")}</span>
                </span>
              </span>
              <span className="family-builder-card-foot">
                <span>v{artifact.versioning.current_version}</span>
                {at && !Number.isNaN(at.getTime()) ? (
                  <span className="family-builder-card-date">
                    <ClockIcon size={12} />
                    {at.toLocaleString(locale === "zh" ? "zh-TW" : "en-US")}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <>
      {head(actions, state.kind === "loaded" ? state.revitVersion : null)}
      {createError ? <p className="settings-msg err">{createError}</p> : null}
      {body}
    </>
  );
}
